import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ButtonGroup from "../Button/Group";
import { css } from "@emotion/react";
import { useFileUploader, UseFileUploaderProps } from "./useFileUploader";
import Icon from "../Icon";
import UploaderFileLists from "./FileLists";
import { UploaderProps } from "./types";

/**
 * Hook for managing a file dropzone, including drag-and-drop and clipboard paste functionality.
 * Wraps the useFileUploader hook to provide additional drag-and-drop and clipboard paste functionality.
 *
 * @example
 * const [dropzoneRef, dropzone] = useFileDropzone({ canPaste: true });
 *
 * return (
 *   <div ref={dropzoneRef}>
 *     <textarea />
 *     <dropzone.Files />
 *     <dropzone.Overlay />
 *   </div>
 * );
 */
export function useFileDropzone<
  ProcessedFile = File,
  ElemRef extends HTMLElement = HTMLDivElement
>({
  canPaste = true,
  disabled = false,
  ...props
}: UseFileDropzoneProps<ProcessedFile>) {
  const fileUploader = useFileUploader<ProcessedFile>(props);
  const ref = useRef<ElemRef>(null);

  // Bounding rect of the ref while a drag is in progress; null when idle.
  const [dragRect, setDragRect] = useState<DOMRect | null>(null);
  // dragenter/dragleave fire for every descendant, so count depth to know when
  // the pointer has actually left the ref rather than crossed a child boundary.
  const dragDepth = useRef(0);
  const isDragging = dragRect !== null;

  const getClipboardFiles = useCallback((event: ClipboardEvent) => {
    const items = event.clipboardData?.items || [];
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
    return files;
  }, []);

  const uploadFilesFromClipboard = useCallback(
    (event: ClipboardEvent) => {
      if (!canPaste || disabled) {
        return;
      }
      const files = getClipboardFiles(event);
      if (files.length > 0) {
        event.preventDefault();
        fileUploader.onFileUpload(files);
      }
    },
    [fileUploader.onFileUpload, getClipboardFiles, canPaste, disabled]
  );

  const uploadFilesFromDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      if (disabled) {
        return;
      }
      const files = event.dataTransfer?.files;
      if (files?.length) {
        fileUploader.onFileUpload(files);
      }
    },
    [fileUploader.onFileUpload, disabled]
  );

  // A drop target must cancel dragenter/dragover, otherwise the browser never
  // fires `drop` and instead opens the dropped file in a new tab.
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const onDragEnter = (event: DragEvent) => {
      event.preventDefault();
      dragDepth.current += 1;
      if (!disabled && dragDepth.current === 1) {
        setDragRect(el.getBoundingClientRect());
      }
    };
    const onDragOver = (event: DragEvent) => event.preventDefault();
    const onDragLeave = () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) {
        setDragRect(null);
      }
    };
    const onDrop = (event: DragEvent) => {
      dragDepth.current = 0;
      setDragRect(null);
      uploadFilesFromDrop(event);
    };

    el.addEventListener("dragenter", onDragEnter);
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);

    return () => {
      el.removeEventListener("dragenter", onDragEnter);
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [ref, disabled, uploadFilesFromDrop]);

  // The overlay is position: fixed off the ref's bounding rect, so re-measure
  // while it's shown in case the page scrolls or resizes mid-drag.
  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }
    const update = () => {
      const el = ref.current;
      if (el) {
        setDragRect(el.getBoundingClientRect());
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isDragging, ref]);

  // A file dropped outside the dropzone would otherwise be opened by the browser
  // in a new tab. Cancel the page-level default so a stray drop is a no-op.
  useEffect(() => {
    const preventDefault = (event: DragEvent) => event.preventDefault();
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);

    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !canPaste) {
      return undefined;
    }
    el.addEventListener("paste", uploadFilesFromClipboard);

    return () => {
      el.removeEventListener("paste", uploadFilesFromClipboard);
    };
  }, [ref, canPaste, uploadFilesFromClipboard]);

  /**
   * Renders the drag overlay when a file is being dragged over the dropzone.
   * @param children The content to render inside the drag overlay.
   * @returns The drag overlay component.
   */
  const Overlay: FC<{ children?: ReactNode }> = useCallback(
    ({ children }) => {
      if (!dragRect) {
        return null;
      }
      // Portal to the owning document's body so the fixed overlay escapes any
      // transformed ancestor and lands in the same document as the dropzone when it lives (possibly inside an iframe).
      const target = ref.current?.ownerDocument.body ?? document.body;
      return createPortal(
        <DropzoneOverlay dragRect={dragRect}>{children}</DropzoneOverlay>,
        target
      );
    },
    [dragRect]
  );

  Overlay.displayName = "DropzoneOverlay";

  const Files: FC<
    {
      className?: string;
    } & Pick<UploaderProps, "listVariant">
  > = useCallback(
    ({ className, listVariant }) => {
      return (
        !fileUploader.isEmpty && (
          <div
            className={`kit-dropzone-files${className ? ` ${className}` : ""}`}
          >
            <UploaderFileLists
              successFiles={fileUploader.successFiles}
              processingFiles={fileUploader.processingFiles}
              fileErrors={fileUploader.fileErrors}
              onDelete={fileUploader.onDelete}
              onReselect={fileUploader.onReselect}
              listVariant={listVariant}
            />
          </div>
        )
      );
    },
    [
      fileUploader.isEmpty,
      fileUploader.successFiles,
      fileUploader.processingFiles,
      fileUploader.fileErrors,
      fileUploader.onDelete,
      fileUploader.onReselect
    ]
  );

  Files.displayName = "DropzoneFiles";

  return [
    ref,
    {
      ...fileUploader,
      isDragging,
      Overlay,
      Files
    }
  ] as const;
}

export type UseFileDropzoneProps<ProcessedFile> =
  UseFileUploaderProps<ProcessedFile> & {
    /**
     * Whether the dropzone should allow pasting files from the clipboard.
     * @default true
     */
    canPaste?: boolean;
    /**
     * When true, the drag overlay is never shown.
     * @default false
     */
    disabled?: boolean;
  };

const dropzoneStyles = {
  overlay: css`
    position: fixed;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed var(--color-border-primary);
    border-radius: var(--spacing-4);
    background: var(--color-background-high-contrast);
    pointer-events: none;
    z-index: 9999;
  `,
  label: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `
};

function DropzoneOverlay({
  children = (
    <ButtonGroup>
      <Icon name="Upload" size="medium" fill="var(--color-splash-primary)" />
      <span>Drop your files here</span>
    </ButtonGroup>
  ),
  dragRect
}: {
  children: ReactNode;
  dragRect: DOMRect;
}) {
  return (
    <div
      className="kit-FileDropzone"
      css={dropzoneStyles.overlay}
      style={{
        top: dragRect.top,
        left: dragRect.left,
        width: dragRect.width,
        height: dragRect.height
      }}
    >
      {children}
    </div>
  );
}
